import { useState } from 'react'
import {
    Box,
    Modal,
    Paper,
    Typography,
    TextField,
    Alert,
    Button,
    Stack,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import CloseIcon from '@mui/icons-material/Close'
import { Org, ReviewBody, zReviewBody } from '@types'
import API from '@enums/api'
import useSession from '@hooks/useSession'

type Props = {
    org: Org | undefined
    status?: 'approved' | 'denied'
    onClose: () => void
    onReviewed: () => void
}

export default function ReviewModal({
    org,
    status,
    onClose,
    onReviewed,
}: Props) {
    const [denialReason, setDenialReason] = useState('')
    const [notes, setNotes] = useState<string>('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const session = useSession()!

    function handleClose() {
        setDenialReason('')
        setNotes('')
        setError(null)
        onClose()
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault()
        if (!org || !status) return
        if (status === 'denied' && !denialReason.trim()) return
        setLoading(true)
        setError(null)

        const body: ReviewBody =
            status === 'approved'
                ? { reviewed_by: session.self.email, status: 'approved', notes }
                : {
                      reviewed_by: session.self.email,
                      status: 'denied',
                      denial_reason: denialReason,
                      notes,
                  }

        const { data, error } = zReviewBody.safeParse(body)

        if (error) {
            setLoading(false)
            setError(error.message)
            console.error(error)
            return
        }

        const response = await API.ha.orgs.review(org.id, data)

        if (!response.ok) {
            setLoading(false)
            setError('Failed to submit review. Please try again.')
            console.error(response.error)
            return
        }

        onReviewed()
        handleClose()
        setLoading(false)
    }

    return (
        <Modal
            aria-labelledby="modal-title"
            open={!!status}
            onClose={handleClose}
            sx={{ my: 'auto' }}
        >
            <Box display="flex" justifyContent="center" alignItems="center">
                <Paper
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                    }}
                >
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems={'center'}
                        mb={2}
                    >
                        <Typography id="modal-title" variant="h5">
                            Review Organisation
                        </Typography>
                        <Button aria-label="Close" onClick={handleClose}>
                            <CloseIcon sx={{ width: 20, height: 20 }} />
                        </Button>
                    </Stack>

                    <form
                        onSubmit={handleSubmit}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 16,
                        }}
                    >
                        {status === 'denied' && (
                            <TextField
                                label="Denial reason"
                                placeholder="This will be sent to the org's admin..."
                                required
                                multiline
                                minRows={2}
                                value={denialReason}
                                onChange={(e) =>
                                    setDenialReason(e.target.value)
                                }
                                fullWidth
                            />
                        )}

                        <TextField
                            label="Notes (optional)"
                            placeholder="Only Habitat Learn staff will see this..."
                            multiline
                            minRows={2}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            fullWidth
                        />

                        {error && <Alert severity="error">{error}</Alert>}

                        <LoadingButton
                            type="submit"
                            variant="contained"
                            loading={loading}
                            disabled={
                                !status ||
                                (status === 'denied' && !denialReason.trim())
                            }
                        >
                            Submit review
                        </LoadingButton>
                    </form>
                </Paper>
            </Box>
        </Modal>
    )
}
